import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
  writeBatch,
  serverTimestamp,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useNotificationsStore } from '@/stores/notifications'
import type { CRMState } from './state'
import type { Prospect, Activity, ProspectStage, ActivityType } from '@/types'

export interface CreateProspectData {
  companyName: string
  contactName: string
  email: string
  phone?: string
  industry?: string
  source?: string
  stage?: ProspectStage
  estimatedValue?: number
  notes?: string
  assignedTo?: string
}

export interface UpdateProspectData {
  companyName?: string
  contactName?: string
  email?: string
  phone?: string
  industry?: string
  source?: string
  stage?: ProspectStage
  estimatedValue?: number
  notes?: string
  assignedTo?: string
}

export interface CreateActivityData {
  prospectId?: string
  organizationId?: string
  type: ActivityType
  title: string
  description?: string
  date: Date
  duration?: number
}

const PROSPECTS_COLLECTION = 'prospects'
const ACTIVITIES_COLLECTION = 'activities'

const convertTimestampToDate = (data: Record<string, unknown>): Record<string, unknown> => {
  const result = { ...data }
  const dateFields = ['createdAt', 'updatedAt', 'date']

  dateFields.forEach((field) => {
    if (result[field] instanceof Timestamp) {
      result[field] = (result[field] as Timestamp).toDate()
    }
  })

  return result
}

export const createActions = (state: CRMState) => {
  const authStore = useAuthStore()
  const notificationsStore = useNotificationsStore()
  let globalUnsubscribe: Unsubscribe | null = null
  let snapshotPausedUntil = 0 // Pause snapshot to avoid overwriting optimistic updates

  const pauseSnapshot = (ms = 1500) => {
    snapshotPausedUntil = Date.now() + ms
  }

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return
    state.isLoading = true
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []
    const q = query(collection(db, PROSPECTS_COLLECTION), ...baseConstraints, orderBy('createdAt', 'desc'))
    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      // Skip snapshot if we just did an optimistic update (avoids board flicker)
      if (Date.now() < snapshotPausedUntil) return
      state.prospects = snapshot.docs.map((d) => {
        const data = convertTimestampToDate(d.data())
        return {
          id: d.id,
          tenantId: (d.data() as Record<string, unknown>).tenantId as string | undefined,
          ...data,
        } as Prospect
      })
      state.isLoading = false
    }, (error) => {
      console.error('CRM subscription error:', error)
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  // ==========================================
  // Prospects Actions
  // ==========================================

  const fetchProspects = async () => {
    state.isLoading = true
    state.error = null

    try {
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []

      const q = query(collection(db, PROSPECTS_COLLECTION), ...baseConstraints, orderBy('createdAt', 'desc'))
      const querySnapshot = await getDocs(q)

      state.prospects = querySnapshot.docs.map((doc) => {
        const data = convertTimestampToDate(doc.data())
        return {
          id: doc.id,
          tenantId: (doc.data() as Record<string, unknown>).tenantId as string | undefined,
          ...data,
        } as Prospect
      })
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error fetching prospects'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const fetchProspectById = async (id: string): Promise<Prospect | null> => {
    state.isLoading = true
    state.error = null

    try {
      const docRef = doc(db, PROSPECTS_COLLECTION, id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = convertTimestampToDate(docSnap.data())
        const prospect = {
          id: docSnap.id,
          tenantId: (docSnap.data() as Record<string, unknown>).tenantId as string | undefined,
          ...data,
        } as Prospect
        state.selectedProspect = prospect
        return prospect
      }
      return null
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error fetching prospect'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const createProspect = async (data: CreateProspectData): Promise<Prospect> => {
    state.isLoading = true
    state.error = null

    try {
      const now = new Date()
      const userId = authStore.user?.id || ''

      const prospectData = {
        ...getTenantData(),
        companyName: data.companyName,
        contactName: data.contactName,
        email: data.email,
        phone: data.phone || null,
        industry: data.industry || null,
        source: data.source || null,
        stage: data.stage || 'prospect',
        estimatedValue: data.estimatedValue || null,
        notes: data.notes || null,
        assignedTo: data.assignedTo || null,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
        createdBy: userId,
        updatedBy: userId,
      }

      const docRef = await addDoc(collection(db, PROSPECTS_COLLECTION), prospectData)

      const newProspect: Prospect = {
        id: docRef.id,
        companyName: data.companyName,
        contactName: data.contactName,
        email: data.email,
        phone: data.phone,
        industry: data.industry,
        source: data.source,
        stage: data.stage || 'prospect',
        estimatedValue: data.estimatedValue,
        notes: data.notes,
        assignedTo: data.assignedTo,
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        updatedBy: userId,
      }

      state.prospects.unshift(newProspect)

      // Notify admins + assigned user about new prospect
      await notificationsStore.notifyAdminsAndStakeholders(
        [data.assignedTo].filter(Boolean) as string[],
        'prospect_created',
        'Nuevo prospecto',
        `Se ha creado el prospecto "${data.companyName}"`,
        'prospect',
        docRef.id
      )

      return newProspect
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error creating prospect'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const updateProspect = async (id: string, data: UpdateProspectData): Promise<Prospect> => {
    state.error = null
    pauseSnapshot()

    try {
      const now = new Date()
      const userId = authStore.user?.id || ''

      const updateData: Record<string, unknown> = {
        updatedAt: Timestamp.fromDate(now),
        updatedBy: userId,
      }

      // Handle optional fields - convert undefined to null for Firestore
      if (data.companyName !== undefined) updateData.companyName = data.companyName
      if (data.contactName !== undefined) updateData.contactName = data.contactName
      if (data.email !== undefined) updateData.email = data.email
      if (data.phone !== undefined) updateData.phone = data.phone || null
      if (data.industry !== undefined) updateData.industry = data.industry || null
      if (data.source !== undefined) updateData.source = data.source || null
      if (data.stage !== undefined) updateData.stage = data.stage
      if (data.estimatedValue !== undefined) updateData.estimatedValue = data.estimatedValue || null
      if (data.notes !== undefined) updateData.notes = data.notes || null
      if (data.assignedTo !== undefined) updateData.assignedTo = data.assignedTo || null

      const docRef = doc(db, PROSPECTS_COLLECTION, id)
      await updateDoc(docRef, updateData)

      // Update local state
      const index = state.prospects.findIndex((p) => p.id === id)
      if (index !== -1) {
        state.prospects[index] = {
          ...state.prospects[index],
          ...data,
          updatedAt: now,
          updatedBy: userId,
        }
      }

      if (state.selectedProspect?.id === id) {
        state.selectedProspect = {
          ...state.selectedProspect,
          ...data,
          updatedAt: now,
          updatedBy: userId,
        }
      }

      // Notify assigned user about stage change
      const existingProspect = state.prospects[index] || state.selectedProspect
      if (data.stage && existingProspect) {
        const stakeholders = [existingProspect.assignedTo].filter(Boolean) as string[]
        if (stakeholders.length > 0) {
          await notificationsStore.notifyUsers(
            stakeholders,
            'prospect_stage_changed',
            'Prospecto actualizado',
            `"${existingProspect.companyName}" cambió a etapa "${data.stage}"`,
            'prospect',
            id
          )
        }
      }

      return existingProspect!
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error updating prospect'
      throw error
    }
  }

  const deleteProspect = async (id: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const docRef = doc(db, PROSPECTS_COLLECTION, id)
      await deleteDoc(docRef)

      state.prospects = state.prospects.filter((p) => p.id !== id)

      if (state.selectedProspect?.id === id) {
        state.selectedProspect = null
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error deleting prospect'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const updateProspectStage = async (id: string, stage: ProspectStage): Promise<Prospect> => {
    return updateProspect(id, { stage })
  }

  const convertToOrganization = async (
    prospectId: string,
    organizationId: string
  ): Promise<Prospect> => {
    state.isLoading = true
    state.error = null

    try {
      const now = new Date()
      const userId = authStore.user?.id || ''

      const docRef = doc(db, PROSPECTS_COLLECTION, prospectId)
      await updateDoc(docRef, {
        convertedToOrganizationId: organizationId,
        stage: 'won',
        updatedAt: Timestamp.fromDate(now),
        updatedBy: userId,
      })

      const index = state.prospects.findIndex((p) => p.id === prospectId)
      if (index !== -1) {
        state.prospects[index] = {
          ...state.prospects[index],
          convertedToOrganizationId: organizationId,
          stage: 'won',
          updatedAt: now,
          updatedBy: userId,
        }
      }

      if (state.selectedProspect?.id === prospectId) {
        state.selectedProspect = {
          ...state.selectedProspect,
          convertedToOrganizationId: organizationId,
          stage: 'won',
          updatedAt: now,
          updatedBy: userId,
        }
      }

      return state.prospects[index] || state.selectedProspect!
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error converting prospect'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  // ==========================================
  // Activities Actions
  // ==========================================

  const fetchActivitiesByProspect = async (prospectId: string): Promise<Activity[]> => {
    state.isLoading = true
    state.error = null

    try {
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []

      const q = query(
        collection(db, ACTIVITIES_COLLECTION),
        ...baseConstraints,
        where('prospectId', '==', prospectId),
        orderBy('date', 'desc')
      )
      const querySnapshot = await getDocs(q)

      const activities = querySnapshot.docs.map((doc) => {
        const data = convertTimestampToDate(doc.data())
        return {
          id: doc.id,
          tenantId: (doc.data() as Record<string, unknown>).tenantId as string | undefined,
          ...data,
        } as Activity
      })

      state.activities = activities
      return activities
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error fetching activities'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const createActivity = async (data: CreateActivityData): Promise<Activity> => {
    state.isLoading = true
    state.error = null

    try {
      const now = new Date()
      const userId = authStore.user?.id || ''

      const activityData = {
        ...getTenantData(),
        prospectId: data.prospectId || null,
        organizationId: data.organizationId || null,
        type: data.type,
        title: data.title,
        description: data.description || null,
        date: Timestamp.fromDate(data.date),
        duration: data.duration || null,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
        createdBy: userId,
        updatedBy: userId,
      }

      const docRef = await addDoc(collection(db, ACTIVITIES_COLLECTION), activityData)

      const newActivity: Activity = {
        id: docRef.id,
        prospectId: data.prospectId,
        organizationId: data.organizationId,
        type: data.type,
        title: data.title,
        description: data.description,
        date: data.date,
        duration: data.duration,
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        updatedBy: userId,
      }

      state.activities.unshift(newActivity)
      return newActivity
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error creating activity'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const deleteActivity = async (id: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const docRef = doc(db, ACTIVITIES_COLLECTION, id)
      await deleteDoc(docRef)

      state.activities = state.activities.filter((a) => a.id !== id)
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error deleting activity'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  // ==========================================
  // Board Order
  // ==========================================

  const updateProspectBoardOrder = async (
    prospectIds: string[],
    stage: ProspectStage
  ): Promise<void> => {
    const userId = authStore.user?.id || 'system'
    pauseSnapshot()

    const batch = writeBatch(db)
    prospectIds.forEach((prospectId, index) => {
      batch.update(doc(db, PROSPECTS_COLLECTION, prospectId), {
        boardOrder: index,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })
    })
    await batch.commit()

    // Update local state
    prospectIds.forEach((prospectId, index) => {
      const prospect = state.prospects.find((p) => p.id === prospectId)
      if (prospect) {
        prospect.boardOrder = index
      }
    })
  }

  // ==========================================
  // Filters
  // ==========================================

  const setFilters = (newFilters: Partial<CRMState['filters']>) => {
    Object.assign(state.filters, newFilters)
  }

  const clearFilters = () => {
    state.filters.search = ''
    state.filters.stage = ''
    state.filters.assignedTo = ''
    state.filters.source = ''
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    // Prospects
    fetchProspects,
    fetchProspectById,
    createProspect,
    updateProspect,
    deleteProspect,
    updateProspectStage,
    updateProspectBoardOrder,
    convertToOrganization,
    // Activities
    fetchActivitiesByProspect,
    createActivity,
    deleteActivity,
    // Filters
    setFilters,
    clearFilters,
  }
}

export type CRMActions = ReturnType<typeof createActions>
